import { format } from "date-fns";
import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import useAlert from "../../../../hooks/useAlert";
import React, { useEffect, useState } from "react";

import { Box } from "@mui/system";
import { Button, Grid, Stack } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/Download";

import Title from "../../../common/Title";
import DataTable from "../../../common/DataTable";

import useAxios from "../../../../hooks/useAxios";
import {
  __getVSExportList,
  __getVSExportFile,
  __vs_export,
} from "../../../../api/sim";

const VSExportList = () => {
  const [data, setData] = useState([]);
  const { setAlertMessage } = useAlert();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 100,
    total: 1,
  });
  const [response, call, loading] = useAxios({ ...__getVSExportList });
  const [fileResponse, fileCall, fileLoading] = useAxios({
    ...__getVSExportFile,
  });
  const [exportResponse, exportCall, exportLoading] = useAxios({
    ...__vs_export,
  });

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page: currentPage,
        page_size: pageSize,
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (response && response.status) {
      setMetaData({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
      });
      setData(response.data.data);
    }
  }, [response]);

  useEffect(() => {
    if (exportResponse && exportResponse.status) {
      setAlertMessage({
        type: "success",
        message: exportResponse.data.message,
      });
    }
  }, [exportResponse]);

  useEffect(() => {
    if (fileResponse && fileResponse.status) {
      fileDownload(fileResponse.data, `vs-export-${new Date().toISOString()}.csv`);
    }
  }, [fileResponse]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  const handleDownload = (fileId) => {
    fileCall({
      url: `sim/vs_export/${fileId}`,
      responseType: "blob",
    });
  };

  const handleExport = () => {
    exportCall();
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    {
      field: "filename",
      headerName: "Filename",
      minWidth: 200,
      flex: 3,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 200,
      filterable: false,
      flex: 3,
      sortable: false,
      valueGetter: (params) =>
        params.row.created_at &&
        `${format(new Date(params.row.created_at), "MMMM d, y hh:mm a")}`,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 150,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          disabled={fileLoading || params.row.status !== "completed"}
          onClick={() => handleDownload(params.row.id)}
          startIcon={<FileDownloadIcon />}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Title variant="h5" content="VS Export History" gutterBottom />
      <Grid container justifyContent="flex-start" spacing={2}>
        <Grid item>
          <Stack spacing={2} mb={2} direction="row">
            <Button
              variant="contained"
              color="primary"
              disabled={exportLoading}
              onClick={handleExport}
            >
              {exportLoading ? "Exporting..." : "Export Sim Info"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {response ? (
        <>
          <DataTable
            disableColumnFilter
            loading={loading}
            data={data}
            columns={columns}
            metaData={metaData}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            filterMode="client"
            client={true}
          />
        </>
      ) : (
        <Title content="Please wait..." variant="subtitle2" gutterBottom />
      )}
    </Box>
  );
};

export default VSExportList;


